import {
  ManagedApplianceList,
  ConnectionDevices,
  PackageAutonomyState,
  PackageConnection,
  PackageConnectionList,
  PackageCatalogue,
  PackagePlan,
  PackageChangeResult,
  PackageInstance,
  PackageInstanceDetail,
  PackageInstanceList,
  PackageOsSupport,
  ServerPackages,
  type AutonomyLevel,
  type ConnectProvider,
  type CreatePackageConnectionRequest,
  type InputValues,
  type PackageConnectChoice,
  type Ownership,
} from "@cloudserver/contracts";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "./client.js";

/*
 * Packages («Приложения», docs/packages.md): the catalogue, installed
 * instances, one instance with its revisions and journal, a server's
 * packages, and every change. All under the project's `packages` key, which
 * the `package.changed` event refreshes (`events.ts`).
 */

export const packageKeys = {
  all: (projectId: string) => ["projects", projectId, "packages"] as const,
  catalogue: (projectId: string) => ["projects", projectId, "packages", "catalogue"] as const,
  instances: (projectId: string) => ["projects", projectId, "packages", "instances"] as const,
  instance: (projectId: string, id: string) =>
    ["projects", projectId, "packages", "instance", id] as const,
  server: (projectId: string, serverId: string) =>
    ["projects", projectId, "packages", "server", serverId] as const,
  autonomy: (projectId: string) => ["projects", projectId, "packages", "autonomy"] as const,
  appliances: (projectId: string) => ["projects", projectId, "packages", "appliances"] as const,
  osSupport: (projectId: string, packageId: string) =>
    ["projects", projectId, "packages", "os-support", packageId] as const,
  devices: (projectId: string, id: string) =>
    ["projects", projectId, "packages", "devices", id] as const,
  connections: ["package-connections"] as const,
};

const P = (projectId: string) => `/projects/${projectId}`;

export const usePackageCatalogue = (projectId: string) =>
  useQuery({
    queryKey: packageKeys.catalogue(projectId),
    queryFn: () => api(`${P(projectId)}/packages`, { schema: PackageCatalogue }),
  });

/** Whether a package runs on each server's release (its vendor's repository, asked by the API). */
export const usePackageOsSupport = (projectId: string, packageId: string) =>
  useQuery({
    queryKey: packageKeys.osSupport(projectId, packageId),
    queryFn: () =>
      api(`${P(projectId)}/packages/${packageId}/os-support`, { schema: PackageOsSupport }),
    select: (answer) => new Map(answer.servers.map((s) => [s.serverId, s])),
  });

export const usePackageInstances = (projectId: string) =>
  useQuery({
    queryKey: packageKeys.instances(projectId),
    queryFn: () => api(`${P(projectId)}/package-instances`, { schema: PackageInstanceList }),
    select: (list) => list.instances,
  });

export const usePackageInstance = (projectId: string, id: string) =>
  useQuery({
    queryKey: packageKeys.instance(projectId, id),
    queryFn: () =>
      api(`${P(projectId)}/package-instances/${id}`, { schema: PackageInstanceDetail }),
    // A lease ends within minutes, an appliance is created or removed in a few: look again meanwhile.
    refetchInterval: (query) => {
      const data = query.state.data;
      if (data?.instance.phase === "applying" || data?.instance.phase === "removing") return 3000;
      return data?.lease ? 5000 : false;
    },
  });

export const useServerPackages = (projectId: string, serverId: string) =>
  useQuery({
    queryKey: packageKeys.server(projectId, serverId),
    queryFn: () => api(`${P(projectId)}/servers/${serverId}/packages`, { schema: ServerPackages }),
  });

/**
 * What an appliance package would create for these inputs, with its price
 * (docs/packages.md "Resources"); a 422 names the input to change.
 */
export const usePackagePlan = (
  projectId: string,
  packageId: string,
  inputs: InputValues,
  enabled: boolean,
) =>
  useQuery({
    queryKey: [...packageKeys.all(projectId), "plan", packageId, inputs] as const,
    queryFn: () =>
      api(`${P(projectId)}/packages/${packageId}/plan`, {
        method: "POST",
        body: { inputs },
        schema: PackagePlan,
      }),
    enabled,
    retry: false,
  });

/** The project's managed appliances: servers packages created and manage (ADR 0002 A). */
export const useAppliances = (projectId: string) =>
  useQuery({
    queryKey: packageKeys.appliances(projectId),
    queryFn: () => api(`${P(projectId)}/appliances`, { schema: ManagedApplianceList }),
    select: (list) => list.appliances,
  });

/** «Забрать себе»: the appliance becomes the customer's server. */
export const useTakeOver = (projectId: string) =>
  usePackageMutation(
    projectId,
    ({ id, key, sshKeyId }: { id: string; key: string; sshKeyId: string }) =>
      api(`${P(projectId)}/package-instances/${id}/resources/${key}/take-over`, {
        method: "POST",
        body: { sshKeyId },
        schema: PackageInstance,
      }),
  );

/** «Вернуть как было» for an appliance changed at the provider. */
export const useRestoreResource = (projectId: string) =>
  usePackageMutation(projectId, ({ id, key }: { id: string; key: string }) =>
    api(`${P(projectId)}/package-instances/${id}/resources/${key}/restore`, {
      method: "POST",
      schema: PackageInstance,
    }),
  );

export const usePackageAutonomy = (projectId: string) =>
  useQuery({
    queryKey: packageKeys.autonomy(projectId),
    queryFn: () => api(`${P(projectId)}/packages/autonomy`, { schema: PackageAutonomyState }),
  });

/** Every change refreshes the project's package queries (and the server pages that show them). */
function usePackageMutation<T, R>(projectId: string, fn: (input: T) => Promise<R>) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: fn,
    onSettled: () => client.invalidateQueries({ queryKey: packageKeys.all(projectId) }),
  });
}

const base = (projectId: string, packageId: string) =>
  `${P(projectId)}/packages/${packageId}/instances`;

export const useInstallPackage = (projectId: string, packageId: string) =>
  usePackageMutation(
    projectId,
    (body: {
      serverId?: string;
      inputs: InputValues;
      ownership?: Ownership;
      connect?: PackageConnectChoice;
    }) =>
      api(base(projectId, packageId), {
        method: "POST",
        body,
        schema: PackageChangeResult,
        idempotencyKey: crypto.randomUUID(),
      }),
  );

/** The changes of one instance of a package (its own routes and actions). */
export function useInstanceChanges(projectId: string, instance: PackageInstance) {
  const url = `${base(projectId, instance.packageId)}/${instance.id}`;
  const configure = usePackageMutation(
    projectId,
    (body: { inputs: InputValues; ownership?: Ownership }) =>
      api(url, { method: "PUT", body, schema: PackageChangeResult }),
  );
  const rollback = usePackageMutation(projectId, (revision: number) =>
    api(`${url}/rollback`, { method: "POST", body: { revision }, schema: PackageChangeResult }),
  );
  const revert = usePackageMutation(projectId, () =>
    api(`${url}/revert`, { method: "POST", schema: PackageChangeResult }),
  );
  const action = usePackageMutation(
    projectId,
    ({ id, params }: { id: string; params: InputValues }) =>
      api(`${url}/actions/${id.replaceAll("_", "-")}`, {
        method: "POST",
        body: { params },
        schema: PackageChangeResult,
      }),
  );
  // 204 when it is gone; 200 with `stopped` when files were edited by hand. `deleteData`:
  // the app's data goes too (only packages whose data policy is `ask`).
  const remove = usePackageMutation(
    projectId,
    ({
      overwrite,
      deleteData = false,
    }: {
      overwrite: boolean;
      deleteData?: boolean;
    }): Promise<PackageChangeResult | undefined> => {
      const query = new URLSearchParams();
      if (overwrite) query.set("overwrite", "true");
      if (deleteData) query.set("data", "delete");
      const search = query.toString();
      return api(`${url}${search ? `?${search}` : ""}`, {
        method: "DELETE",
        schema: PackageChangeResult,
      });
    },
  );
  return { configure, rollback, revert, action, remove };
}

export const useCheckPackage = (projectId: string) =>
  usePackageMutation(projectId, (id: string) =>
    api(`${P(projectId)}/package-instances/${id}/check`, {
      method: "POST",
      schema: PackageChangeResult,
    }),
  );

export const useFreezeInstance = (projectId: string) =>
  usePackageMutation(projectId, ({ id, frozen }: { id: string; frozen: boolean }) =>
    api(`${P(projectId)}/package-instances/${id}/freeze`, {
      method: "PUT",
      body: { frozen },
      schema: PackageInstance,
    }),
  );

export const useInstanceAutonomy = (projectId: string) =>
  usePackageMutation(projectId, ({ id, level }: { id: string; level: AutonomyLevel | null }) =>
    api(`${P(projectId)}/package-instances/${id}/autonomy`, {
      method: "PUT",
      body: { level },
      schema: PackageInstance,
    }),
  );

export const useProjectAutonomy = (projectId: string) =>
  usePackageMutation(projectId, (level: AutonomyLevel | null) =>
    api(`${P(projectId)}/packages/autonomy`, {
      method: "PUT",
      body: { level },
      schema: PackageAutonomyState,
    }),
  );

export const useFreezeServer = (projectId: string, serverId: string) =>
  usePackageMutation(projectId, (frozen: boolean) =>
    api(`${P(projectId)}/servers/${serverId}/packages/freeze`, {
      method: "PUT",
      body: { frozen },
      schema: ServerPackages,
    }),
  );

/** «Передать файл под управление панели» (adopted) or give it back to the customer. */
export const useAdoptCustomerFile = (projectId: string) =>
  usePackageMutation(
    projectId,
    ({ id, path, adopted }: { id: string; path: string; adopted: boolean }) =>
      api(`${P(projectId)}/package-instances/${id}/customer-files`, {
        method: "PUT",
        body: { path, adopted },
        schema: PackageInstance,
      }),
  );

/**
 * Approve or reject an Autopilot card; `restart`: a card of a customer's
 * file whose change needs a restart runs the app's restart step after it.
 */
export const useDecideProposal = (projectId: string) =>
  usePackageMutation(
    projectId,
    ({ id, approve, restart }: { id: string; approve: boolean; restart?: boolean }) =>
      api(`${P(projectId)}/package-proposals/${id}/${approve ? "approve" : "reject"}`, {
        method: "POST",
        ...(approve && restart !== undefined ? { body: { restart } } : {}),
        schema: PackageChangeResult,
      }),
  );

// --- Connect flows (docs/packages.md, "Connect flows") --------------------------------------

/** The organisation's connections of a provider (Tailscale OAuth clients); secrets never come back. */
export const usePackageConnections = (provider: ConnectProvider) =>
  useQuery({
    queryKey: [...packageKeys.connections, provider],
    queryFn: () =>
      api(`/package-connections?provider=${provider}`, { schema: PackageConnectionList }),
    select: (list) => list.connections,
  });

export function useCreateConnection() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (body: CreatePackageConnectionRequest) =>
      api("/package-connections", { method: "POST", body, schema: PackageConnection }),
    onSettled: () => client.invalidateQueries({ queryKey: packageKeys.connections }),
  });
}

export function useDeleteConnection() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api(`/package-connections/${id}`, { method: "DELETE" }),
    onSettled: () => client.invalidateQueries({ queryKey: packageKeys.connections }),
  });
}

/** The devices of an instance's OAuth connection (its tailnet), read when asked. */
export const useConnectionDevices = (projectId: string, instanceId: string, enabled: boolean) =>
  useQuery({
    queryKey: packageKeys.devices(projectId, instanceId),
    queryFn: () =>
      api(`${P(projectId)}/package-instances/${instanceId}/connection/devices`, {
        schema: ConnectionDevices,
      }),
    enabled,
  });
